import { 
  useNetworkMetrics, 
  useTopology, 
  useCircuits,
  DataContainer,
} from '@estream/sdk-browser/react';
import type { NetworkMetrics, TopologySnapshot } from '@estream/sdk-browser';
import './Dashboard.css';

export default function Dashboard() {
  return (
    <div className="dashboard animate-in">
      {/* Metrics Overview */}
      <section className="card metrics-section">
        <h2 className="card-title">Network Overview</h2>
        <MetricsGrid />
      </section>
      
      {/* Two column layout */}
      <div className="dashboard-grid">
        {/* Quick Topology */}
        <section className="card">
          <div className="card-header">
            <h2 className="card-title">Active Nodes</h2>
            <a href="/topology" className="card-link">View All →</a>
          </div>
          <TopologyQuickView />
        </section>
        
        {/* Recent Activity */}
        <section className="card">
          <div className="card-header">
            <h2 className="card-title">Recent Activity</h2>
            <a href="/circuits" className="card-link">View All →</a>
          </div>
          <RecentActivity />
        </section>
      </div>
    </div>
  );
}

function MetricsGrid() {
  const result = useNetworkMetrics();
  
  return (
    <DataContainer {...result}>
      {(metrics: NetworkMetrics) => (
        <div className="metrics-grid">
          <MetricCard 
            label="Messages/sec" 
            value={formatBigNumber(metrics.messagesPerSecond)}
            icon="📨"
          />
          <MetricCard 
            label="Active Nodes" 
            value={metrics.totalNodes.toString()}
            icon="🖥️"
          />
          <MetricCard 
            label="Connections" 
            value={metrics.totalConnections.toString()}
            icon="🔗"
          />
          <MetricCard 
            label="Avg Latency" 
            value={`${metrics.avgLatencyMs}ms`}
            icon="⏱️"
            status={metrics.avgLatencyMs < 100 ? 'good' : metrics.avgLatencyMs < 300 ? 'warning' : 'error'}
          />
          <MetricCard 
            label="Active Circuits" 
            value={metrics.activeCircuits.toString()}
            icon="⚡"
          />
          <MetricCard 
            label="Pending Proposals" 
            value={metrics.pendingProposals.toString()}
            icon="🔐"
            status={metrics.pendingProposals > 0 ? 'warning' : 'good'}
          />
        </div>
      )}
    </DataContainer>
  );
}

function MetricCard({ 
  label, 
  value, 
  icon, 
  status 
}: { 
  label: string; 
  value: string; 
  icon: string;
  status?: 'good' | 'warning' | 'error';
}) {
  return (
    <div className={`metric-card ${status || ''}`}>
      <span className="metric-icon">{icon}</span>
      <div className="metric-content">
        <span className="metric-value">{value}</span>
        <span className="metric-label">{label}</span>
      </div>
    </div>
  );
}

function TopologyQuickView() {
  const result = useTopology();
  
  return (
    <DataContainer {...result}>
      {(topology: TopologySnapshot) => (
        <div className="node-list">
          {topology.nodes.slice(0, 5).map((node) => (
            <div key={node.id} className="node-item">
              <span className={`status-dot ${node.status.toLowerCase()}`} />
              <span className="node-id">{node.id.slice(0, 12)}...</span>
              <span className="node-type badge badge-primary">{node.type}</span>
              <span className="node-region">{node.region}</span>
            </div>
          ))}
          {topology.nodes.length === 0 && (
            <div className="empty-state">No nodes connected</div>
          )}
        </div>
      )}
    </DataContainer>
  );
}

function RecentActivity() {
  const result = useCircuits();
  
  return (
    <DataContainer {...result}>
      {(circuits) => {
        const allItems = [
          ...(circuits.pending || []).slice(0, 3).map((p: unknown) => ({ 
            ...(p as object), 
            status: 'pending' 
          })),
          ...(circuits.deployed || []).slice(0, 3).map((d) => ({ 
            id: d.circuitId, 
            type: d.type, 
            status: 'deployed' 
          })),
        ];
        
        if (allItems.length === 0) {
          return <div className="empty-state">No recent activity</div>;
        }
        
        return (
          <div className="activity-list">
            {allItems.map((item, i) => (
              <div key={i} className="activity-item">
                <span className={`badge badge-${item.status === 'pending' ? 'warning' : 'success'}`}>
                  {item.status}
                </span>
                <span className="activity-type">{(item as { type?: string }).type || 'Unknown'}</span>
                <span className="activity-time">Just now</span>
              </div>
            ))}
          </div>
        );
      }}
    </DataContainer>
  );
}

function formatBigNumber(n: bigint | number): string {
  const num = typeof n === 'bigint' ? Number(n) : n;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toString();
}
